import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '@contexts/LanguageContext';
import { useAcknowledgePatchLogs, useDeferPatchLogs, useUnreadPatchLogs } from '@hooks/patch-logs/use-patch-logs';
import { getPatchLogUserId, hasDeferredThisSession, markDeferredThisSession } from './deferral-storage';
import { useOptionalPatchLogGate } from './gate-context';
import { resolvePatchLogCopy } from './i18n';
import { PatchLogModal } from './PatchLogModal';
import { isPatchLogLocale, type PatchLogLocale, type PatchLogPublic } from './types';

const publishedAtMs = (log: PatchLogPublic) => {
  const ms = log.publishedAt ? Date.parse(log.publishedAt) : Number.NaN;
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Login-first blocking modal host (Issue 6).
 *
 * Decision, in order:
 *  1. The unread query has settled. While it is in flight the gate reports
 *     `isBlocking` so the onboarding tour waits instead of racing the modal.
 *  2. The user has not chosen "Remind me later" in this browser session
 *     (sessionStorage flag, cleared on logout).
 *  3. There is at least one unread log left after this session's acknowledgments.
 * Any failure to fetch counts as "nothing unread" — the app must never be
 * blocked by a broken feed.
 */
export const PatchLogGate = () => {
  const { language, setLanguage } = useLanguage();
  const gate = useOptionalPatchLogGate();
  const { data, isPending } = useUnreadPatchLogs();
  const acknowledge = useAcknowledgePatchLogs();
  const defer = useDeferPatchLogs();

  const userId = getPatchLogUserId();
  const [deferredThisSession, setDeferredThisSession] = useState(() => hasDeferredThisSession(userId));
  // Ids acknowledged in this mount; keeps the modal closed during the refetch
  // that follows the acknowledgment.
  const [handledIds, setHandledIds] = useState<number[]>([]);

  const locale: PatchLogLocale = isPatchLogLocale(language) ? language : 'id';

  const logs = useMemo(() => {
    const unread = (data?.logs ?? []).filter((log) => !handledIds.includes(log.id));
    // The server already sends newest first; keep that order even if it does not.
    return [...unread].sort((a, b) => publishedAtMs(b) - publishedAtMs(a));
  }, [data, handledIds]);

  const opened = !deferredThisSession && logs.length > 0;
  const blocking = isPending || opened;

  const setBlocking = gate?.setBlocking;
  useEffect(() => {
    setBlocking?.(blocking);
    return () => setBlocking?.(false);
  }, [blocking, setBlocking]);

  const patchLogIds = useMemo(() => logs.map((log) => log.id), [logs]);

  const handleConfirm = useCallback(() => {
    if (acknowledge.isPending || patchLogIds.length === 0) {
      return;
    }
    acknowledge.mutate(
      { patchLogIds, localeRead: locale, scrolledToBottom: true },
      {
        onSuccess: () => setHandledIds((previous) => [...previous, ...patchLogIds]),
        onError: (error) => {
          console.warn('[patch-logs] acknowledge failed', error);
          toast.error(resolvePatchLogCopy(locale, 'ackFailed'));
        },
      }
    );
  }, [acknowledge, patchLogIds, locale]);

  const handleDefer = useCallback(() => {
    if (patchLogIds.length === 0) {
      return;
    }
    // Optimistic: the user asked not to be blocked right now, so close first.
    // If the server misses the deferral the log simply returns at next login.
    markDeferredThisSession(userId);
    setDeferredThisSession(true);
    defer.mutate(
      { patchLogIds },
      {
        onError: (error) => {
          console.warn('[patch-logs] defer failed', error);
          toast.warning(resolvePatchLogCopy(locale, 'deferFailed'));
        },
      }
    );
  }, [defer, patchLogIds, userId, locale]);

  const handleLocaleChange = useCallback((next: PatchLogLocale) => setLanguage(next), [setLanguage]);

  if (!opened) {
    return null;
  }

  return (
    <PatchLogModal
      opened
      logs={logs}
      locale={locale}
      onLocaleChange={handleLocaleChange}
      onConfirm={handleConfirm}
      onDefer={handleDefer}
      confirming={acknowledge.isPending}
    />
  );
};

export default PatchLogGate;
